/**
 * Reiter „Auswertung“: die Kosten der Baseline als Pivot. Die Ebenen wählt man über die Breadcrumbs
 * (jedes Glied einzeln änderbar), der Baum lädt eine Ebene je Aufklappen, ein Knoten zeigt seine Zahlen
 * und die Positionen dahinter — deren Zuordnung zum Leistungskatalog man hier bestätigt. Rechts die
 * Buckets eines Szenarios: ein Knoten wird hineingezogen und wird dort zur Regel; jeder Knoten zeigt im
 * Balken, wie er sich auf die Buckets verteilt (tri-server docs/fm-baselining-entwurf.md 7).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { ChevronDown, ChevronRight, Plus, RefreshCw, Sparkles, X } from "lucide-react";

import { authenticationAtom } from "../../State";
import { useBaseliningAPI, type CatalogueNode, type PivotChild, type PivotLine, type PivotSummary, type Scenario, type ScenarioTotals } from "../../BaseliningAPI";
import BucketPanel from "./BucketPanel";
import BenchmarkPanel from "./BenchmarkPanel";
import DetailSection from "./DetailSection";
import {
  type Dimension,
  NODE_DRAG_TYPE,
  NONE,
  appendNext,
  choicesAt,
  distributionSegments,
  formatEuro,
  formatShare,
  nodeFilter,
  nodeId,
  pathFrom,
  periodFrom,
  quickPeriods,
  samePeriod,
  removeAt,
  replaceAt,
  swatchFor,
} from "./pivotModel";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

type Loaded = { children: PivotChild[] } | "loading" | { error: string };

export default function AnalysisTab() {
  const { t, i18n } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string = authentication?.customer ?? "";
  const api = useBaseliningAPI(customer);
  const [searchParams, setSearchParams] = useSearchParams();
  const path = pathFrom(searchParams.get("path"));
  const period = periodFrom(searchParams.get("from"), searchParams.get("to"));
  const periodKey = `${period.from ?? ""}..${period.to ?? ""}`;

  const [levels, setLevels] = useState<Record<string, Loaded>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<string[] | null>(null);
  const [builtAt, setBuiltAt] = useState<string | null>(null);
  const [busy, setBusy] = useState<"rebuild" | "classify" | "repropose" | null>(null);
  const [generation, setGeneration] = useState(0);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [totals, setTotals] = useState<ScenarioTotals | null>(null);
  const scenarioId = searchParams.get("scenario") || null;
  const scenario = scenarios.find((s) => s.id === scenarioId) ?? null;

  const setPath = (next: Dimension[]) => {
    const params = new URLSearchParams(searchParams);
    params.set("path", next.join(","));
    setSearchParams(params, { replace: true });
    setLevels({});
    setExpanded(new Set());
    setSelected(null);
  };

  /** Ein anderer Zeitraum baut den Baum neu auf; die Auswahl bleibt, ihre Zahlen werden neu geholt. */
  const setPeriod = (next: { from?: string; to?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.from) params.set("from", next.from);
    else params.delete("from");
    if (next.to) params.set("to", next.to);
    else params.delete("to");
    setSearchParams(params, { replace: true });
    setLevels({});
    setExpanded(new Set());
  };

  const label = useCallback(
    (dimension: Dimension, value: string, fallback: string) => {
      if (value === NONE) return t("baselining.analysis.none");
      if (dimension === "budgetType") return t(`baselining.budgetType.${value}`, value);
      if (dimension === "activity") return t(`baselining.activity.${value}`, value);
      if (dimension === "presentation") return t(`baselining.presentation.${value}`, value);
      if (dimension === "extraction") return t(`baselining.extraction.${value}`, value);
      return fallback || value;
    },
    [t],
  );

  const load = useCallback(
    async (values: string[]) => {
      const id = nodeId(values);
      const dimension = path[values.length];
      if (!dimension) return;
      setLevels((l) => ({ ...l, [id]: "loading" }));
      try {
        const result = await api.pivot(dimension, nodeFilter(path, values), scenarioId, period);
        setLevels((l) => ({ ...l, [id]: { children: result.children } }));
        if (values.length === 0) setBuiltAt(result.builtAt);
      } catch (e: any) {
        setLevels((l) => ({ ...l, [id]: { error: e?.message ?? t("baselining.analysis.loadFailed") } }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customer, path.join(","), scenarioId, periodKey],
  );

  useEffect(() => {
    if (customer) void load([]);
  }, [customer, load, generation]);

  const loadScenarios = useCallback(async () => {
    try {
      const list = await api.listScenarios();
      setScenarios(list);
      return list;
    } catch (e: any) {
      toast.error(e?.message ?? t("baselining.analysis.loadFailed"));
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const loadTotals = useCallback(async () => {
    if (!scenarioId) return setTotals(null);
    try {
      setTotals(await api.scenarioStatus(scenarioId, period));
    } catch {
      setTotals(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, scenarioId, periodKey]);

  useEffect(() => {
    if (customer) void loadScenarios();
  }, [customer, loadScenarios]);

  useEffect(() => {
    void loadTotals();
  }, [loadTotals]);

  const selectScenario = (id: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (id) params.set("scenario", id);
    else params.delete("scenario");
    setSearchParams(params, { replace: true });
    setLevels({});
    setExpanded(new Set());
  };

  /**
   * Nach einer Regel: alles Geladene neu holen, ohne den Baum zuzuklappen — wer Knoten für Knoten zieht,
   * will an derselben Stelle weiterarbeiten.
   */
  const reloadLoaded = () => {
    for (const id of Object.keys(levels)) void load(JSON.parse(id));
  };

  const onBucketsChanged = async (select?: string) => {
    await loadScenarios();
    if (select !== undefined && select !== scenarioId) {
      selectScenario(select || null); // die Abhängigkeit von `scenarioId` lädt Baum und Summen neu
      return;
    }
    reloadLoaded();
    void loadTotals();
  };

  /** Die Beschriftung eines Knotens samt seiner Vorfahren — so steht er an der Regel. */
  const captionOf = (values: string[]) => values.map((v, i) => label(path[i], v, findLabel(levels, path, values, i))).join(" › ");
  const bucketOrder = (scenario?.buckets ?? []).map((b) => b.id);
  const bucketSlots = Object.fromEntries((scenario?.buckets ?? []).map((b) => [b.id, b.slot]));

  const toggle = (values: string[]) => {
    const id = nodeId(values);
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        if (!levels[id]) void load(values);
      }
      return next;
    });
  };

  const refreshAll = () => {
    setLevels({});
    setExpanded(new Set());
    setGeneration((g) => g + 1);
    void loadTotals();
  };

  const run = async (kind: "rebuild" | "classify" | "repropose") => {
    setBusy(kind);
    try {
      if (kind === "rebuild") {
        const r = await api.rebuildFacts();
        toast.success(t("baselining.analysis.rebuilt", { invoices: r.invoices, facts: r.facts }));
      } else {
        const r = await api.classify(kind === "repropose");
        toast.success(t("baselining.analysis.classified", { byRule: r.byRule, proposed: r.proposed, unanswered: r.unanswered }));
      }
      refreshAll();
    } catch (e: any) {
      toast.error(e?.message ?? t("baselining.analysis.actionFailed"));
    } finally {
      setBusy(null);
    }
  };

  const renderLevel = (values: string[], depth: number): JSX.Element | null => {
    const state = levels[nodeId(values)];
    if (!state || state === "loading") return <p className="py-1 text-xs text-muted-foreground" style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}>{t("baselining.analysis.loading")}</p>;
    if ("error" in state) return <p className="py-1 text-xs text-destructive" style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}>{state.error}</p>;
    const dimension = path[depth];
    return (
      <>
        {state.children.map((child) => {
          const childValues = [...values, child.value];
          const id = nodeId(childValues);
          const canOpen = depth + 1 < path.length;
          const open = expanded.has(id);
          const active = selected && nodeId(selected) === id;
          return (
            <div key={id}>
              <div
                role="treeitem"
                aria-expanded={canOpen ? open : undefined}
                aria-selected={!!active}
                className={`relative flex cursor-pointer items-center gap-2 border-b px-2 py-1.5 text-sm hover:bg-muted/50 ${active ? "bg-muted" : ""}`}
                style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}
                onClick={() => setSelected(childValues)}
                draggable={!!scenario}
                onDragStart={(e) => {
                  e.dataTransfer.setData(NODE_DRAG_TYPE, JSON.stringify({ filter: nodeFilter(path, childValues), label: captionOf(childValues) }));
                  e.dataTransfer.effectAllowed = "copy";
                }}
                title={scenario ? t("baselining.buckets.dragHint") : undefined}
              >
                <button
                  type="button"
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canOpen) toggle(childValues);
                  }}
                  disabled={!canOpen}
                  aria-label={open ? t("baselining.catalogue.collapse") : t("baselining.catalogue.expand")}
                >
                  {canOpen ? open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" /> : null}
                </button>
                <span className={`flex-1 truncate ${child.value === NONE ? "italic text-muted-foreground" : ""}`}>{label(dimension, child.value, child.label)}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{t("baselining.analysis.invoiceCount", { count: child.invoices })}</span>
                <span className="w-28 shrink-0 text-right font-mono text-xs">{formatEuro(child.amount, i18n.language)}</span>
                {scenario && (
                  <DistributionBar
                    segments={distributionSegments(child.buckets, bucketOrder)}
                    swatch={(key) => swatchFor(key, bucketSlots)}
                    name={(key) => scenario.buckets.find((b) => b.id === key)?.name ?? t(key === "_conflict" ? "baselining.buckets.conflict" : "baselining.buckets.rest")}
                  />
                )}
              </div>
              {open && renderLevel(childValues, depth + 1)}
            </div>
          );
        })}
      </>
    );
  };

  const root = levels[nodeId([])];
  const empty = root && root !== "loading" && !("error" in root) && root.children.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">{t("baselining.analysis.levels")}</span>
        {path.map((dimension, index) => (
          <div key={`${dimension}-${index}`} className="flex items-center gap-1">
            {index > 0 && <span className="text-muted-foreground">›</span>}
            <Select value={dimension} onValueChange={(value) => setPath(replaceAt(path, index, value as Dimension))}>
              <SelectTrigger className="h-8 w-auto gap-1 px-2 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {choicesAt(path, index).map((d) => (
                  <SelectItem key={d} value={d}>
                    {t(`baselining.dimension.${d}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {path.length > 1 && (
              <button type="button" className="rounded p-0.5 text-muted-foreground hover:bg-muted" onClick={() => setPath(removeAt(path, index))} aria-label={t("baselining.analysis.removeLevel")}>
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        <Button variant="ghost" size="sm" onClick={() => setPath(appendNext(path))} aria-label={t("baselining.analysis.addLevel")}>
          <Plus className="h-4 w-4" />
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {builtAt && <span className="text-xs text-muted-foreground">{t("baselining.analysis.builtAt", { when: new Date(builtAt).toLocaleString(i18n.language) })}</span>}
          <Button variant="outline" size="sm" onClick={() => void run("classify")} disabled={!!busy}>
            <Sparkles className="mr-1 h-4 w-4" />
            {busy === "classify" ? t("baselining.analysis.classifying") : t("baselining.analysis.classify")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void run("repropose")} disabled={!!busy} title={t("baselining.analysis.reproposeHint")}>
            {busy === "repropose" ? t("baselining.analysis.classifying") : t("baselining.analysis.repropose")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void run("rebuild")} disabled={!!busy}>
            <RefreshCw className="mr-1 h-4 w-4" />
            {busy === "rebuild" ? t("baselining.analysis.rebuilding") : t("baselining.analysis.rebuild")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">{t("baselining.analysis.period")}</span>
        {quickPeriods(new Date()).map((q) => (
          <Button key={q.key} size="sm" variant={samePeriod(period, q) ? "secondary" : "ghost"} className="h-8" onClick={() => setPeriod(q)}>
            {q.key === "all" ? t("baselining.analysis.periodAll") : q.key === "last12" ? t("baselining.analysis.periodLast12") : q.key}
          </Button>
        ))}
        <Input
          type="month"
          className="h-8 w-40"
          value={period.from ?? ""}
          onChange={(e) => setPeriod(periodFrom(e.target.value || null, period.to ?? null))}
          aria-label={t("baselining.analysis.periodFrom")}
        />
        <span className="text-muted-foreground">–</span>
        <Input
          type="month"
          className="h-8 w-40"
          value={period.to ?? ""}
          onChange={(e) => setPeriod(periodFrom(period.from ?? null, e.target.value || null))}
          aria-label={t("baselining.analysis.periodTo")}
        />
      </div>

      {empty ? (
        <Alert>
          <AlertDescription>{t("baselining.analysis.empty")}</AlertDescription>
        </Alert>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="rounded-md border" role="tree" aria-label={t("baselining.tabs.analysis")}>
            {renderLevel([], 0)}
          </div>
          <div className="space-y-4">
            <BucketPanel scenarios={scenarios} scenarioId={scenarioId} totals={totals} onSelect={selectScenario} onChanged={(select) => void onBucketsChanged(select)} />
            {selected ? (
              <NodeDetail
                key={`${nodeId(selected)}-${generation}-${periodKey}`}
                title={selected.map((v, i) => label(path[i], v, findLabel(levels, path, selected, i))).join(" › ")}
                filter={nodeFilter(path, selected)}
                period={period}
                onChanged={refreshAll}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t("baselining.analysis.selectHint")}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Wie sich ein Knoten auf die Buckets verteilt: ein schmaler gestapelter Balken unten an der Zeile, je
 * Segment 2 px Abstand, der Name und Betrag im Tooltip.
 */
function DistributionBar({
  segments,
  swatch,
  name,
}: {
  segments: Array<{ key: string; share: number; amount: number }>;
  swatch: (key: string) => string;
  name: (key: string) => string;
}) {
  const { i18n } = useTranslation();
  if (segments.length === 0) return null;
  return (
    <div className="absolute bottom-0 left-8 right-2 flex h-1 gap-[2px]" aria-hidden>
      {segments.map((s) => (
        <div
          key={s.key}
          className={`h-full rounded-sm ${swatch(s.key)}`}
          style={{ width: `${s.share * 100}%` }}
          title={`${name(s.key)}: ${formatEuro(s.amount, i18n.language)} (${formatShare(s.share)})`}
        />
      ))}
    </div>
  );
}

/** The label a node got from the server, looked up in the level it was loaded in. */
const findLabel = (levels: Record<string, Loaded>, path: Dimension[], values: string[], index: number): string => {
  const state = levels[nodeId(values.slice(0, index))];
  if (!state || state === "loading" || "error" in state) return values[index];
  return state.children.find((c) => c.value === values[index])?.label ?? values[index];
};

function NodeDetail({
  title,
  filter,
  period,
  onChanged,
}: {
  title: string;
  filter: Record<string, string>;
  period: { from?: string; to?: string };
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useBaseliningAPI(authentication?.customer ?? "");
  const [summary, setSummary] = useState<PivotSummary | null>(null);
  const [lines, setLines] = useState<PivotLine[] | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.summary(filter, period), api.lines(filter, period)])
      .then(([s, l]) => {
        if (cancelled) return;
        setSummary(s);
        setLines(l);
      })
      .catch((e) => !cancelled && setError(e?.message ?? t("baselining.analysis.loadFailed")));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;
  if (!summary) return <p className="text-sm text-muted-foreground">{t("baselining.analysis.loading")}</p>;

  const chart = {
    options: {
      chart: { type: "bar" as const, toolbar: { show: false }, animations: { enabled: false } },
      xaxis: { categories: summary.months.map((m) => m.month) },
      yaxis: { labels: { formatter: (v: number) => formatEuro(v, i18n.language) } },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (v: number) => formatEuro(v, i18n.language) } },
      colors: ["#3b82f6"],
    },
    series: [{ name: t("baselining.analysis.amount"), data: summary.months.map((m) => m.amount) }],
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label={t("baselining.analysis.amount")} value={formatEuro(summary.amount, i18n.language)} />
          <Stat label={t("baselining.analysis.invoices")} value={String(summary.invoices)} />
          <Stat label={t("baselining.analysis.suppliers")} value={String(summary.suppliers)} />
          <Stat label={t("baselining.analysis.classifiedShare")} value={formatShare(summary.classifiedShare)} />
        </div>
        {summary.months.length > 0 && <ReactApexChart options={chart.options} series={chart.series} type="bar" height={200} />}
        <div className="flex flex-wrap gap-2 text-xs">
          {summary.years.map((y) => (
            <Badge key={y.year} variant="outline">{y.year}: {formatEuro(y.amount, i18n.language)}</Badge>
          ))}
          {summary.budgetTypes.map((b) => (
            <Badge key={b.budgetType} variant="secondary">
              {b.budgetType === NONE ? t("baselining.analysis.noBudgetType") : t(`baselining.budgetType.${b.budgetType}`, b.budgetType)}: {formatEuro(b.amount, i18n.language)}
            </Badge>
          ))}
        </div>
        {(summary.periodAssumedShare > 0 || summary.grossOnlyShare > 0) && (
          <p className="text-xs text-muted-foreground">
            {t("baselining.analysis.dataQuality", { periodAssumed: formatShare(summary.periodAssumedShare), grossOnly: formatShare(summary.grossOnlyShare) })}
          </p>
        )}
        {filter.site && filter.site !== NONE && !filter.site.startsWith("project:") && <BenchmarkPanel site={filter.site} root={filter.category3 ?? filter.category2 ?? filter.category1} period={period} />}
        {lines && <LinesList lines={lines} onChanged={onChanged} />}
      </CardContent>
    </Card>
  );
}

/** Eine Kennzahl: kleine Beschriftung oben, die Zahl groß und unten bündig, damit eine zweizeilige Beschriftung die Reihe nicht verschiebt. */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col justify-between gap-1 rounded-lg border bg-muted/30 px-3 py-2.5">
      <div className="text-xs leading-tight text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums tracking-tight">{value}</div>
    </div>
  );
}

const flatten = (nodes: CatalogueNode[], depth = 0): Array<{ node: CatalogueNode; depth: number }> =>
  nodes.flatMap((n) => [{ node: n, depth }, ...flatten(n.children, depth + 1)]);

function LinesList({ lines, onChanged }: { lines: PivotLine[]; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useBaseliningAPI(authentication?.customer ?? "");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [catalogue, setCatalogue] = useState<Array<{ node: CatalogueNode; depth: number }>>([]);
  const [categoryKey, setCategoryKey] = useState<string>("");
  const [activity, setActivity] = useState<string>("");
  const [createRule, setCreateRule] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getCatalogue().then((c) => setCatalogue(flatten(c.nodes))).catch(() => setCatalogue([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const node = useMemo(() => catalogue.find((c) => c.node.key === categoryKey)?.node, [catalogue, categoryKey]);

  const pick = (line: PivotLine) => {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(line.id)) next.delete(line.id);
      else next.add(line.id);
      return next;
    });
    if (!categoryKey && line.categoryKey) {
      setCategoryKey(line.categoryKey);
      setActivity(line.activity ?? "");
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      await api.confirmLines({ lineIds: Array.from(chosen), categoryKey, activity: activity || null, createRule });
      toast.success(t("baselining.analysis.confirmed", { count: chosen.size }));
      setChosen(new Set());
      onChanged();
    } catch (e: any) {
      toast.error(e?.message ?? t("baselining.analysis.actionFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (lines.length === 0)
    return (
      <DetailSection title={t("baselining.analysis.lines")}>
        <p className="text-xs text-muted-foreground">{t("baselining.analysis.noLines")}</p>
      </DetailSection>
    );

  return (
    <DetailSection title={t("baselining.analysis.lines")} aside={t("baselining.analysis.linesCount", { count: lines.length })}>
      <div className="max-h-80 overflow-auto rounded-md border">
        {lines.map((line) => (
          <label key={line.id} className="flex cursor-pointer items-start gap-2 border-b px-2 py-1.5 last:border-b-0 hover:bg-muted/40">
            <Checkbox checked={chosen.has(line.id)} onCheckedChange={() => pick(line)} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="truncate" title={line.text}>{line.text || t("baselining.analysis.noText")}</div>
              <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
                <span>{line.supplier}</span>
                {line.invoiceNumber && <span>· {line.invoiceNumber}</span>}
                {line.categoryName && (
                  <Badge variant={line.status === "confirmed" ? "secondary" : "outline"} className="h-5 px-1 text-[11px]">
                    {line.categoryName}
                    {line.status === "proposed" ? ` · ${t("baselining.analysis.proposed")}${line.confidence != null ? ` ${Math.round(line.confidence * 100)} %` : ""}` : ""}
                    {line.by === "rule" ? ` · ${t("baselining.analysis.byRule")}` : ""}
                  </Badge>
                )}
              </div>
            </div>
            <span className="shrink-0 font-mono text-xs">{formatEuro(line.amount, i18n.language)}</span>
          </label>
        ))}
      </div>
      {chosen.size > 0 && (
        <div className="space-y-2 rounded-md border p-2">
          <div className="text-xs text-muted-foreground">{t("baselining.analysis.assignChosen", { count: chosen.size })}</div>
          <Select value={categoryKey} onValueChange={(v) => { setCategoryKey(v); setActivity(""); }}>
            <SelectTrigger className="h-8"><SelectValue placeholder={t("baselining.analysis.chooseCategory")} /></SelectTrigger>
            <SelectContent className="max-h-80">
              {catalogue.map(({ node: n, depth }) => (
                <SelectItem key={n.key} value={n.key}>
                  <span style={{ paddingLeft: `${depth * 0.75}rem` }}>{n.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {node && node.activities.length > 0 && (
            <Select value={activity || NONE} onValueChange={(v) => setActivity(v === NONE ? "" : v)}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("baselining.analysis.noActivity")}</SelectItem>
                {node.activities.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{i18n.language.startsWith("en") ? a.en : a.de}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={createRule} onCheckedChange={(v) => setCreateRule(v === true)} />
            {t("baselining.analysis.createRule")}
          </label>
          <Button size="sm" onClick={() => void confirm()} disabled={saving || !categoryKey}>{t("baselining.analysis.confirm")}</Button>
        </div>
      )}
    </DetailSection>
  );
}
